import { FunctionComponent, useRef, useCallback } from 'react';
import {
	Autocomplete as MuiAutocomplete,
	TextField,
	styled,
	InputBaseProps,
	FormControl,
	MenuItem,
} from '@mui/material';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { ColorPalette } from 'shared/consts/colorPalette';
import { InputError } from 'shared/ui/InputError';
import { InputLabel } from 'shared/ui/InputLabel';
import { SelectOption } from 'shared/ui/Select/types';

export type AutocompleteOption = {
	value: string | undefined | null;
	label: React.ReactNode | string;
};

export type AutocompleteProps = InputBaseProps & {
	name: string;
	label: string;
	errorText?: string;
	options: AutocompleteOption[];
	onChange?: (newVal: AutocompleteOption | null) => void;
	onSearch?: (value: string) => void;
	value?: AutocompleteOption;
	defaultValue?: AutocompleteOption;
	loadMore?: boolean;
	onFetchMore?: () => void;
	loading?: boolean;
};

const Autocomplete: FunctionComponent<AutocompleteProps> = ({
	sx,
	name,
	label,
	required,
	fullWidth = true,
	errorText,
	placeholder,
	defaultValue,
	options,
	onChange,
	onSearch,
	loadMore,
	onFetchMore,
	loading,
}) => {
	const { t } = useTranslation();
	const observer = useRef<IntersectionObserver | null>(null);

	const handleSearch = _.debounce((e, value: string) => {
		onSearch && e && onSearch(value);
	}, 500);

	const lastOptionElementRef = useCallback(
		async (node: Element | null) => {
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver(async (entries) => {
				if (entries[0].isIntersecting && loadMore && onFetchMore && !loading) {
					await onFetchMore();
				}
			});
			if (node) observer.current.observe(node);
		},
		[loadMore, onFetchMore],
	);

	return (
		<MuiAutocomplete
			onInputChange={handleSearch}
			options={options}
			defaultValue={defaultValue}
			onChange={(e, newVal) => onChange && onChange(newVal as SelectOption)}
			filterOptions={(options) => options}
			renderInput={(params) => {
				return (
					<FormControl
						variant={'standard'}
						fullWidth={fullWidth}
						sx={{ marginBottom: '40px', ...sx }}
					>
						{label && (
							<InputLabel
								name={name}
								label={label}
								required={required}
								sx={{ position: 'relative' }}
							/>
						)}
						<WhiteBorderTextField
							name={name}
							placeholder={placeholder}
							sx={{
								input: {
									color: ColorPalette.astronaut,
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '18px',
								},
							}}
							{...params}
						/>
						<InputError text={errorText} />
					</FormControl>
				);
			}}
			loading={loading}
			loadingText={loading && t('shared_texts.loading')}
			renderOption={(props, option, { index }) => {
				return (
					<MenuItem {...props} ref={index === options.length - 1 ? lastOptionElementRef : null}>
						{option.label}
					</MenuItem>
				);
			}}
		/>
	);
};

export default Autocomplete;

const WhiteBorderTextField = styled(TextField)`
	fieldset {
		border: 1px solid ${ColorPalette.periwinkle};
		border-radius: 10px;
	}

	& .MuiOutlinedInput-root {
		&.Mui-focused fieldset {
			border: 1px solid ${ColorPalette.periwinkle};
		}
		&:hover fieldset {
			border: 1px solid ${ColorPalette.periwinkle};
		}
	}
`;
